// src/modules/app-views/omnichannel/pages/Init/components/omnichannelSseManager.js
// *******************************************************************************
// SHARED SSE CONNECTION MANAGER
// *******************************************************************************
// Single EventSource connection shared between ChatMenu and Conversation.
// Both components subscribe/unsubscribe to events via this manager.
// Eliminates duplicate SSE connections that exhaust the browser's HTTP/1.1 limit.
// *******************************************************************************

let _es = null;
let _subscriptionId = null;
let _reconnectTimeout = null;
let _connecting = null; // Promise lock to prevent concurrent connects
let _hasDisconnectedOnce = false; // Only call /disconnect once per page load
let _reconnectAttempts = 0; // Reconnect counter for exponential backoff
const MAX_RECONNECT_ATTEMPTS = 10; // Stop reconnecting after this many failures
const BASE_RECONNECT_MS = 2_000; // Start at 2s, doubles each attempt (max ~34min)
// Map<eventType, Set<callback>>
const _listeners = new Map();

// *******************************************************************************
// PUBLIC API
// *******************************************************************************

/**
 * Ensure a single SSE connection is open for this subscription.
 * Safe to call multiple times — only connects once (uses a promise lock).
 */
export async function connect(subscriptionId) {
  if (!subscriptionId) return;

  // Already connected to this subscription
  if (_es && _subscriptionId === subscriptionId && _es.readyState !== EventSource.CLOSED) {
    return;
  }

  // Another call is already connecting — wait for it instead of creating a second connection
  if (_connecting) {
    await _connecting;
    return;
  }

  _connecting = _doConnect(subscriptionId);
  await _connecting;
  _connecting = null;
}

async function _doConnect(subscriptionId) {
  // Close stale local connection if subscription changed
  disconnect();
  _subscriptionId = subscriptionId;
  await _connect(subscriptionId);
}

/**
 * Emit a local event to all subscribers (no server round-trip).
 * Useful for propagating state between components without waiting for SSE.
 */
export function emitLocal(eventType, data) {
  const callbacks = _listeners.get(eventType);
  if (callbacks) {
    for (const cb of callbacks) {
      try { cb(data); } catch { /* listener error */ }
    }
  }
}

/**
 * Close the shared SSE connection and clear all state.
 */
export function disconnect() {
  if (_reconnectTimeout) {
    clearTimeout(_reconnectTimeout);
    _reconnectTimeout = null;
  }
  if (_es) {
    window.__omnichannelSSE?.delete(_es);
    _es.close();
    _es = null;
  }
  _subscriptionId = null;
  _reconnectAttempts = 0;
}

/**
 * Subscribe to an SSE event type. Returns an unsubscribe function.
 *
 * @param {string} eventType - e.g. 'omnichannel:message-received'
 * @param {Function} callback - receives parsed payload.data
 * @returns {Function} unsubscribe
 */
export function subscribe(eventType, callback) {
  if (!_listeners.has(eventType)) {
    _listeners.set(eventType, new Set());
  }
  _listeners.get(eventType).add(callback);

  return () => {
    const set = _listeners.get(eventType);
    if (set) {
      set.delete(callback);
      if (set.size === 0) _listeners.delete(eventType);
    }
  };
}

/**
 * Get current connection state
 */
export function isConnected() {
  return _es && _es.readyState === EventSource.OPEN;
}

// *******************************************************************************
// INTERNAL
// *******************************************************************************

async function _connect(subscriptionId) {
  try {
    const { default: apiConfig } = await import('configs/fetchInterceptor');
    if (_subscriptionId !== subscriptionId) return;

    const baseURL = apiConfig.defaults.baseURL || '';
    window.__fetchInterceptorBaseURL = baseURL;

    const url = `${baseURL}/v1/omnichannel/events/${subscriptionId}`;

    const es = new EventSource(url);
    _es = es;

    if (!window.__omnichannelSSE) window.__omnichannelSSE = new Set();
    window.__omnichannelSSE.add(es);

    // Route all SSE events to registered listeners
    const eventTypes = [
      'omnichannel:message-received',
      'omnichannel:message-sent',
      'omnichannel:message-status-updated',
      'omnichannel:conversation-read',
      'omnichannel:contact-snapshot-updated',
      'omnichannel:conversation-assigned',
      'omnichannel:context-closed',
      'omnichannel:message-reaction',
      'omnichannel:message-media-updated',
      'omnichannel:conversation-archived',
      'omnichannel:source-status-changed',
    ];

    for (const type of eventTypes) {
      es.addEventListener(type, event => {
        try {
          const payload = JSON.parse(event.data);
          const callbacks = _listeners.get(type);
          if (callbacks) {
            for (const cb of callbacks) {
              try { cb(payload.data || {}); } catch { /* listener error */ }
            }
          }
        } catch {
          // Ignore parse errors
        }
      });
    }

    es.addEventListener('heartbeat', () => {
      // Keep alive — no action needed
    });

    es.onopen = () => {
      // Connection established — reset backoff counter
      _reconnectAttempts = 0;
    };

    es.onerror = () => {
      window.__omnichannelSSE?.delete(es);
      es.close();
      _es = null;

      // Exponential backoff with max attempts to avoid exhausting browser connection pool
      _reconnectAttempts++;
      if (_reconnectAttempts > MAX_RECONNECT_ATTEMPTS) {
        console.warn('[Omnichannel SSE] Max reconnect attempts reached, stopping.');
        return;
      }
      const delay = Math.min(BASE_RECONNECT_MS * Math.pow(2, _reconnectAttempts - 1), 60_000);
      _reconnectTimeout = setTimeout(() => _connect(subscriptionId), delay);
    };
  } catch {
    // Backend unreachable — exponential backoff
    _reconnectAttempts++;
    if (_reconnectAttempts > MAX_RECONNECT_ATTEMPTS) return;
    const delay = Math.min(BASE_RECONNECT_MS * Math.pow(2, _reconnectAttempts - 1), 60_000);
    _reconnectTimeout = setTimeout(() => _connect(subscriptionId), delay);
  }
}

// *******************************************************************************
// BEFOREUNLOAD + HMR CLEANUP
// *******************************************************************************

/**
 * Force-disconnect via sendBeacon — guaranteed to execute during page unload.
 * Tells the backend to destroy all SSE sockets immediately so the browser's
 * HTTP/1.1 connection slots are freed before the new page tries to connect.
 */
function forceDisconnectViaBeacon() {
  if (_subscriptionId) {
    try {
      const apiConfig = window.__fetchInterceptorBaseURL || '';
      const url = `${apiConfig}/v1/omnichannel/events/${_subscriptionId}/disconnect`;
      navigator.sendBeacon(url);
    } catch {
      // Best effort — might fail if no baseURL cached yet
    }
  }
  disconnect();
}

// Cache the baseURL as soon as we connect so sendBeacon can use it during unload
async function _cacheBaseURL() {
  try {
    const { default: apiConfig } = await import('configs/fetchInterceptor');
    window.__fetchInterceptorBaseURL = apiConfig.defaults.baseURL || '';
  } catch {
    // Will retry on next connect
  }
}
_cacheBaseURL();

// Close SSE on page unload (F5, tab close, Vite full reload)
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', forceDisconnectViaBeacon);
  // pagehide fires more reliably on mobile and some browsers
  window.addEventListener('pagehide', forceDisconnectViaBeacon);
}

// Vite HMR cleanup — fires when THIS module is hot-replaced
if (import.meta.hot) {
  import.meta.hot.dispose(forceDisconnectViaBeacon);

  // Also accept self to keep the module hot-reloadable
  import.meta.hot.accept();
}
